// Overlay scrollbars of the whole interface (owner 29.10, п.8): hidden at rest; thin and translucent while the pointer is over a
// scrolling block or the block scrolls; thicker and brighter under the pointer. Native bars are hidden in styles.css, and one layer
// above the page draws the bars of the block in use, so every block gets them without code of its own. Only the thumb takes the
// pointer: the rest of the bar's zone lets clicks through to the block's edge. Events only note what happened; styles and layout
// are read once per frame. Text areas and lists keep the native bar (a field scrolls by itself, with the caret).

type Axis = 'y' | 'x';
interface Bar { axis: Axis; track: HTMLDivElement; thumb: HTMLDivElement; el: Element | null; hideAt: number; drag: boolean }

/** How long bars stay after the last scroll of a block the pointer is not over. */
const LINGER = 900;
/** Blocks narrower than this (a row of view tabs) scroll without a bar: a bar there would cover their buttons. */
const MIN_SIDE = 48;
const MIN_THUMB = 24;
/** Inset of the track from the block's corners. */
const INSET = 2;

const overflowOf = (el: Element, axis: Axis) => { const cs = getComputedStyle(el); return axis === 'y' ? cs.overflowY : cs.overflowX; };
const isRoot = (el: Element) => el === document.scrollingElement;

/** Fields with their own native bar (styles.css): the overlay does not draw a second one over them. */
const NATIVE = new Set(['TEXTAREA', 'SELECT']);

/** The block scrolls on this axis: its own overflow allows it and its content is larger. The page itself counts too. */
function scrolls(el: Element, axis: Axis): boolean {
  if (NATIVE.has(el.tagName)) return false;
  const more = axis === 'y' ? el.scrollHeight > el.clientHeight + 1 : el.scrollWidth > el.clientWidth + 1;
  if (!more) return false;
  if (isRoot(el)) return true;
  if ((el as HTMLElement).dataset?.scrollbar === 'none') return false;
  const o = overflowOf(el, axis);
  return o === 'auto' || o === 'scroll' || o === 'overlay';
}

/** The innermost block under `node` that scrolls on the axis. */
function scrollerOf(node: Node | null, axis: Axis): Element | null {
  for (let el = node instanceof Element ? node : node?.parentElement ?? null; el; el = el.parentElement) {
    if (el === document.body) break;
    if (scrolls(el, axis)) return el;
  }
  const root = document.scrollingElement;
  return root && scrolls(root, axis) ? root : null;
}

/** Ancestors that clip a block, kept per block: overflow rules do not change while a block is in use, so they are not read each frame. */
const clipsOf = new WeakMap<Element, Element[]>();
function clipping(el: Element): Element[] {
  let list = clipsOf.get(el);
  if (!list) {
    list = [];
    for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
      const cs = getComputedStyle(p);
      if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') list.push(p);
    }
    clipsOf.set(el, list);
  }
  return list;
}

/** The part of the block's scrolling box that is on screen: clipped by every ancestor that clips and by the window. */
function visibleBox(el: Element): DOMRect | null {
  let l: number, t: number, r: number, b: number;
  if (isRoot(el)) { l = 0; t = 0; r = window.innerWidth; b = window.innerHeight; }
  else {
    const rect = el.getBoundingClientRect();
    l = rect.left + el.clientLeft; t = rect.top + el.clientTop; r = l + el.clientWidth; b = t + el.clientHeight;
    for (const p of clipping(el)) {
      const pr = p.getBoundingClientRect();
      l = Math.max(l, pr.left); t = Math.max(t, pr.top); r = Math.min(r, pr.right); b = Math.min(b, pr.bottom);
    }
    l = Math.max(l, 0); t = Math.max(t, 0); r = Math.min(r, window.innerWidth); b = Math.min(b, window.innerHeight);
  }
  return r - l > 0 && b - t > 0 ? new DOMRect(l, t, r - l, b - t) : null;
}

let installed = false;

/** Starts the overlay bars once for the page; before the body exists it waits for it. */
export function installScrollbars(): void {
  if (installed || typeof window === 'undefined') return;
  if (!document.body) { document.addEventListener('DOMContentLoaded', installScrollbars, { once: true }); return; }
  installed = true;
  const layer = document.createElement('div');
  layer.className = 'cd-sb-layer';
  // Decoration only: the block itself scrolls by keyboard and is what a screen reader reads; the bar repeats its position.
  layer.setAttribute('aria-hidden', 'true');
  const make = (axis: Axis): Bar => {
    const track = document.createElement('div');
    track.className = `cd-sb cd-sb-${axis}`;
    const thumb = document.createElement('div');
    thumb.className = 'cd-sb-thumb';
    track.append(thumb);
    layer.append(track);
    return { axis, track, thumb, el: null, hideAt: 0, drag: false };
  };
  const bars = { y: make('y'), x: make('x') };
  const all = [bars.y, bars.x];
  document.body.append(layer);

  /** The blocks under the pointer, by axis: their bars stay while the pointer is over them or over the bars. */
  const hover: Record<Axis, Element | null> = { y: null, x: null };
  /** What the events noted since the last frame: the element under the pointer, blocks that scrolled or took focus. */
  let pointed: Node | null | undefined;
  const pinged = new Set<Element>();
  let frame = 0;
  let timer = 0;

  /** Places one bar over its block, or hides it. */
  const place = (bar: Bar, now: number) => {
    const el = bar.el;
    const shown = !!el && el.isConnected && (bar.drag || hover[bar.axis] === el || now < bar.hideAt) && scrolls(el, bar.axis);
    const box = shown ? visibleBox(el!) : null;
    const side = box ? (bar.axis === 'y' ? box.height : box.width) : 0;
    if (!box || side < MIN_SIDE || (bar.axis === 'y' ? box.width : box.height) < MIN_SIDE) { bar.track.classList.remove('on'); return; }
    const y = bar.axis === 'y';
    const size = y ? el!.clientHeight : el!.clientWidth;
    const full = y ? el!.scrollHeight : el!.scrollWidth;
    const pos = y ? el!.scrollTop : el!.scrollLeft;
    const len = side - INSET * 2;
    const thumb = Math.max(MIN_THUMB, Math.round(len * (size / full)));
    const at = full > size ? Math.round((len - thumb) * Math.min(1, Math.max(0, pos / (full - size)))) : 0;
    const st = bar.track.style;
    if (y) { st.left = `${box.right}px`; st.top = `${box.top + INSET}px`; st.height = `${len}px`; st.width = ''; bar.thumb.style.height = `${thumb}px`; bar.thumb.style.transform = `translateY(${at}px)`; }
    else { st.top = `${box.bottom}px`; st.left = `${box.left + INSET}px`; st.width = `${len}px`; st.height = ''; bar.thumb.style.width = `${thumb}px`; bar.thumb.style.transform = `translateX(${at}px)`; }
    bar.track.classList.add('on');
  };
  /** A block scrolled or took focus: its bars show for a moment, also by wheel elsewhere, keyboard or touch. */
  const ping = (el: Element, now: number) => {
    for (const b of all) {
      if (b.el === el) { b.hideAt = now + LINGER; continue; }
      if (b.drag || !scrolls(el, b.axis)) continue;
      // A block the pointer is not over takes the bar only when the hovered block has none on this axis, or when the focus
      // is in it: a keyboard scroll shows its bar wherever the pointer rests.
      if (b.el && hover[b.axis] === b.el && !el.contains(document.activeElement)) continue;
      b.el = el; b.hideAt = now + LINGER;
    }
  };
  const update = () => {
    frame = 0;
    const now = performance.now();
    // The block under the pointer is looked up once per frame, however many elements the pointer crossed.
    if (pointed !== undefined && !all.some((b) => b.drag)) {
      for (const b of all) {
        hover[b.axis] = pointed ? scrollerOf(pointed, b.axis) : null;
        // A block scrolled from the keyboard keeps its bar for the moment, whatever the resting pointer crosses.
        const keyed = !!b.el && now < b.hideAt && b.el.contains(document.activeElement);
        if (hover[b.axis] && !keyed) b.el = hover[b.axis];
        else if (!keyed && b.hideAt <= now) b.el = null;
      }
    }
    pointed = undefined;
    for (const el of pinged) ping(el, now);
    pinged.clear();
    for (const b of all) place(b, now);
    // Bars left by a scroll fade on their own; one check after the longest of them is due.
    const next = Math.max(...all.map((b) => b.hideAt)) - now;
    clearTimeout(timer);
    if (next > 0) timer = window.setTimeout(schedule, next + 16);
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };

  document.addEventListener('pointerover', (e) => {
    if (e.pointerType === 'touch' || layer.contains(e.target as Node)) return;
    pointed = e.target as Node;
    schedule();
  }, { passive: true });
  const away = () => { pointed = null; schedule(); };
  document.documentElement.addEventListener('pointerleave', away);
  window.addEventListener('blur', away);
  // A block that leaves the page (a menu or a popover closed under a resting pointer) takes its bar along: no pointer event comes
  // then, so the layer is told by the removal itself. Portals of menus are children of the body.
  new MutationObserver(() => { if (all.some((b) => b.el && !b.el.isConnected)) { for (const b of all) if (b.el && !b.el.isConnected) { b.el = null; if (hover[b.axis] && !hover[b.axis]!.isConnected) hover[b.axis] = null; } schedule(); } })
    .observe(document.body, { childList: true, subtree: true });
  // Content may grow without a scroll or a hover (rows arrive): moving over the block keeps the thumb true.
  document.addEventListener('pointermove', () => { if (hover.y || hover.x) schedule(); }, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('scroll', (e) => {
    const el = e.target instanceof Element ? e.target : document.scrollingElement;
    if (el) { pinged.add(el); schedule(); }
  }, { capture: true, passive: true });
  // Focus by keyboard in a block shows where it stands before the first key scrolls it.
  document.addEventListener('focusin', (e) => {
    const t = e.target as Element;
    if (!t.matches?.(':focus-visible')) return;
    const el = scrollerOf(t, 'y') ?? scrollerOf(t, 'x');
    if (el) { pinged.add(el); schedule(); }
  });

  for (const bar of all) {
    const y = bar.axis === 'y';
    bar.thumb.addEventListener('pointerenter', () => bar.track.classList.add('hot'));
    bar.thumb.addEventListener('pointerleave', () => { if (!bar.drag) bar.track.classList.remove('hot'); });
    // The wheel over the thumb scrolls its block, not the page under the layer.
    bar.thumb.addEventListener('wheel', (e) => {
      if (!bar.el) return;
      e.preventDefault();
      const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? (y ? bar.el.clientHeight : bar.el.clientWidth) : 1;
      bar.el.scrollBy({ left: e.deltaX * k, top: e.deltaY * k });
    }, { passive: false });
    bar.thumb.addEventListener('pointerdown', (e) => {
      const el = bar.el;
      if (!el || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const start = y ? e.clientY : e.clientX;
      const from = y ? el.scrollTop : el.scrollLeft;
      const len = (y ? bar.track.clientHeight : bar.track.clientWidth) - (y ? bar.thumb.offsetHeight : bar.thumb.offsetWidth);
      const ratio = len > 0 ? ((y ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth) / len) : 0;
      bar.drag = true;
      bar.track.classList.add('hot', 'drag');
      bar.thumb.setPointerCapture(e.pointerId);
      const mv = (ev: PointerEvent) => {
        const v = from + ((y ? ev.clientY : ev.clientX) - start) * ratio;
        if (y) el.scrollTop = v; else el.scrollLeft = v;
      };
      const up = () => {
        bar.drag = false;
        bar.track.classList.remove('drag');
        if (!bar.thumb.matches(':hover')) bar.track.classList.remove('hot');
        bar.thumb.removeEventListener('pointermove', mv);
        bar.thumb.removeEventListener('pointerup', up);
        bar.thumb.removeEventListener('pointercancel', up);
        bar.hideAt = performance.now() + LINGER;
        schedule();
      };
      bar.thumb.addEventListener('pointermove', mv);
      bar.thumb.addEventListener('pointerup', up);
      bar.thumb.addEventListener('pointercancel', up);
    });
  }
}
